import { useCallback, useEffect, useState } from 'react';

import './editor-fullscreen.css';

export const EDITOR_FULLSCREEN_CLASS = 'editor-fullscreen';

/**
 * In-app full screen for an embedded editor panel. The panel's own element is stretched over the
 * window with CSS, so the IDE iframe keeps its identity and connection. Escape exits only when the
 * key reaches the surrounding page; keystrokes inside the editor never leave its frame.
 */
export function useEditorFullscreen(active: boolean) {
  const [fullscreen, setFullscreen] = useState(false);
  const exit = useCallback(() => setFullscreen(false), []);
  const toggle = useCallback(() => setFullscreen((current) => !current), []);
  useEffect(() => {
    if (!active) setFullscreen(false);
  }, [active]);
  useEffect(() => {
    if (!fullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"]')) return;
      setFullscreen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.documentElement.setAttribute('data-editor-fullscreen', '');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.documentElement.removeAttribute('data-editor-fullscreen');
    };
  }, [fullscreen]);
  return { fullscreen, toggle, exit, className: fullscreen ? EDITOR_FULLSCREEN_CLASS : '' };
}
